import { Injectable } from "@angular/core";
import { HttpService } from "app/js/src/infrastructure/http/http";
import {
  IClippingStudioRepository,
  ClippingStudioCreateProjectResult,
  ClippingStudioOpenProjectResult,
  PublishMetadataSavePayload,
} from "app/js/src/domain/clipping-studio/i-clipping-studio-repository";
import {
  ClippingStudioAudioEvent,
  ClippingStudioProject,
  ClippingStudioTextEvent,
  DestinationVariant,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { ClippingStudioSubtitleStyle } from "app/js/src/domain/clipping-studio/clipping-studio-transcript";
import { serializeClipPreviewForProjectSave } from "app/js/src/domain/clipping-studio/clip-preview-model";

/** Shape of the `project` object inside the API response (snake_case, JSON-string fields). */
interface ProjectApi {
  media_id: number;
  fps: number;
  title?: string;
  clip_preview?: string;
  main_source?: string;
  publish_metadata_json?: string;
  publish_metadata_overrides_json?: string;
  video_segments?: string;
  markers?: string;
  subtitles?: string;
  subtitle_edits?: string;
  text?: string;
  graphics?: string;
  audio?: string;
  subtitle_style?: string;
  destination_variants?: string;
  appended_mam_segments?: string;
  find_clip_recent_prompts?: string;
  updated_at?: string;
}

/** Top-level payload from POST /api/clipping-studio/projects. */
interface CreateProjectApiResponse {
  publication_uid: string;
}

/** Top-level payload from GET /api/publications/{publicationUid}/open. */
interface OpenProjectApiResponse {
  project_id: number;
  project: ProjectApi;
}

@Injectable({ providedIn: "root" })
export class ClippingStudioHttpRepositoryService extends IClippingStudioRepository {
  private readonly projectsBaseUrl = "clipping-studio/projects";

  constructor(private readonly httpService: HttpService) {
    super();
  }

  async createNewProject(mediaId: number): Promise<ClippingStudioCreateProjectResult> {
    const raw: CreateProjectApiResponse = await this.httpService.post(
      this.projectsBaseUrl,
      { media_id: mediaId },
    );
    return { publicationUid: raw.publication_uid };
  }

  async openProject(publicationUid: string): Promise<ClippingStudioOpenProjectResult> {
    const raw: OpenProjectApiResponse = await this.httpService.get(
      `publications/${publicationUid}/open`,
    );
    return {
      projectId: raw.project_id,
      project: this.mapProject(raw.project),
    };
  }

  async saveProject(
    projectId: number,
    project: ClippingStudioProject,
    publishPayload?: PublishMetadataSavePayload,
  ): Promise<void> {
    const url = `${this.projectsBaseUrl}/${projectId}`;
    const body = this.toSaveRequest(project, publishPayload);
    await this.httpService.put(url, body);
  }

  async submitProjectForPublication(
    projectId: number,
    cloneProject: boolean,
  ): Promise<void> {
    await this.httpService.post(`${this.projectsBaseUrl}/${projectId}/submit`, {
      clone_project: cloneProject,
    });
  }

  private mapProject(api: ProjectApi): ClippingStudioProject {
    return {
      mediaId: api.media_id,
      title: api.title ?? undefined,
      clipPreview: api.clip_preview ?? undefined,
      publishMetadataJson: api.publish_metadata_json ?? undefined,
      publishMetadataOverridesJson: api.publish_metadata_overrides_json ?? undefined,
      mainSource: api.main_source ?? undefined,
      fps: api.fps > 0 ? api.fps : undefined,
      video: this.parseJsonArray(api.video_segments),
      markers: this.parseJsonArray(api.markers),
      subtitles: this.parseJsonArray(api.subtitles),
      subtitleEdits: this.parseJsonOrUndefined(api.subtitle_edits),
      subtitleStyle: this.parseSubtitleStyle(api.subtitle_style),
      text: this.mapApiTextToFrontend(this.parseJsonArray(api.text)),
      graphics: this.parseJsonArray(api.graphics),
      audio: this.parseAudioEvents(api.audio),
      destinationVariants: this.normalizeDestinationVariants(
        this.parseJsonOrUndefined(api.destination_variants),
      ),
      appendedMamSegments: this.parseJsonArray(api.appended_mam_segments),
      findClipRecentPrompts: api.find_clip_recent_prompts ?? undefined,
    };
  }

  private parseSubtitleStyle(json: string | null | undefined): ClippingStudioSubtitleStyle | undefined {
    if (json == null || json === "") return undefined;
    try {
      const raw = JSON.parse(json) as Record<string, unknown>;
      const fontFamily = raw.font_family ?? raw.fontFamily;
      const fontSizeRaw = raw.font_size ?? raw.fontSize;
      const fontSize = typeof fontSizeRaw === "number" ? fontSizeRaw : Number(fontSizeRaw);
      const fontColor = raw.font_color ?? raw.fontColor;
      const backgroundColor = raw.background_color ?? raw.backgroundColor;
      const backgroundStyle = raw.background_style ?? raw.backgroundStyle;
      const position = raw.position;
      if (
        typeof fontFamily !== "string" ||
        Number.isNaN(fontSize) ||
        typeof fontColor !== "string" ||
        typeof backgroundColor !== "string" ||
        (backgroundStyle !== "solid" && backgroundStyle !== "outline" && backgroundStyle !== "dropShadow") ||
        (position !== "top" && position !== "middle" && position !== "bottom")
      ) {
        return undefined;
      }
      return {
        fontFamily,
        fontSize,
        fontColor,
        backgroundColor,
        backgroundStyle,
        position,
      };
    } catch {
      return undefined;
    }
  }

  private serializeSubtitleStyle(style: ClippingStudioSubtitleStyle | undefined): string {
    if (style == null) return this.defaultSubtitleStyleJson();
    return JSON.stringify({
      font_family: style.fontFamily,
      font_size: style.fontSize,
      font_color: style.fontColor,
      background_color: style.backgroundColor,
      background_style: style.backgroundStyle,
      position: style.position,
    });
  }

  /** Minimal valid subtitle style JSON for backend required field when project has no style set. */
  private defaultSubtitleStyleJson(): string {
    return JSON.stringify({
      font_family: "Arial",
      font_size: 24,
      font_color: "#ffffff",
      background_color: "#000000",
      background_style: "solid",
      position: "bottom",
    });
  }

  /**
   * Maps API text events (block structure: typography, style, position or flat) to frontend flat model.
   * If the backend returns block structure, it is flattened; otherwise the item is used as-is with camelCase normalization.
   */
  private mapApiTextToFrontend(apiText: unknown[]): ClippingStudioTextEvent[] {
    return apiText.map((item) => this.mapOneApiTextToFrontend(item as Record<string, unknown>));
  }

  private mapOneApiTextToFrontend(item: Record<string, unknown>): ClippingStudioTextEvent {
    const t = item.typography as Record<string, unknown> | undefined;
    const s = item.style as Record<string, unknown> | undefined;
    const p = item.position as Record<string, unknown> | undefined;
    const hasBlock = t != null || s != null || p != null;

    if (hasBlock) {
      return {
        id: String(item.id ?? ""),
        inMs: Number(item.in_ms ?? item.inMs ?? 0),
        outMs: Number(item.out_ms ?? item.outMs ?? 0),
        text: String(item.text ?? ""),
        fontSize: t != null ? Number(t.font_size ?? t.fontSize) || undefined : this.num(item.fontSize),
        alignment: (t?.alignment ?? item.alignment) as ClippingStudioTextEvent["alignment"],
        bold: t != null ? Boolean(t.bold) : this.bool(item.bold),
        italic: t != null ? Boolean(t.italic) : this.bool(item.italic),
        fontFamily: t != null ? String(t.font_family ?? t.fontFamily ?? "") || undefined : this.str(item.fontFamily),
        fontWeight: t != null ? String(t.font_weight ?? t.fontWeight ?? "") || undefined : this.str(item.fontWeight),
        fontStyle: t != null ? String(t.font_style ?? t.fontStyle ?? "") || undefined : this.str(item.fontStyle),
        fontColor: s != null ? String(s.font_color ?? s.fontColor ?? "") || undefined : this.str(item.fontColor),
        backgroundColor: s != null ? String(s.background_color ?? s.backgroundColor ?? "") || undefined : this.str(item.backgroundColor),
        backgroundPadding: s != null ? Number(s.background_padding ?? s.backgroundPadding) || undefined : this.num(item.backgroundPadding),
        backgroundBorderRadius: s != null ? Number(s.background_border_radius ?? s.backgroundBorderRadius) || undefined : this.num(item.backgroundBorderRadius),
        dropShadow: s != null ? Boolean(s.drop_shadow ?? s.dropShadow) : this.bool(item.dropShadow),
        shadowColor: s != null ? String(s.shadow_color ?? s.shadowColor ?? "") || undefined : this.str(item.shadowColor),
        shadowBlur: s != null ? Number(s.shadow_blur ?? s.shadowBlur) || undefined : this.num(item.shadowBlur),
        shadowOffsetX: s != null ? Number(s.shadow_offset_x ?? s.shadowOffsetX) || undefined : this.num(item.shadowOffsetX),
        shadowOffsetY: s != null ? Number(s.shadow_offset_y ?? s.shadowOffsetY) || undefined : this.num(item.shadowOffsetY),
        x: p != null ? Number(p.x) || undefined : this.num(item.x),
        y: p != null ? Number(p.y) || undefined : this.num(item.y),
        width: p != null ? Number(p.width) || undefined : this.num(item.width),
        height: p != null ? Number(p.height) || undefined : this.num(item.height),
      };
    }

    return {
      id: String(item.id ?? ""),
      inMs: Number(item.in_ms ?? item.inMs ?? 0),
      outMs: Number(item.out_ms ?? item.outMs ?? 0),
      text: String(item.text ?? ""),
      fontSize: this.num(item.fontSize ?? item.font_size),
      alignment: item.alignment as ClippingStudioTextEvent["alignment"] | undefined,
      bold: this.bool(item.bold),
      italic: this.bool(item.italic),
      fontFamily: this.str(item.fontFamily ?? item.font_family),
      fontWeight: this.str(item.fontWeight ?? item.font_weight),
      fontStyle: this.str(item.fontStyle ?? item.font_style),
      fontColor: this.str(item.fontColor ?? item.font_color),
      backgroundColor: this.str(item.backgroundColor ?? item.background_color),
      backgroundPadding: this.num(item.backgroundPadding ?? item.background_padding),
      backgroundBorderRadius: this.num(item.backgroundBorderRadius ?? item.background_border_radius),
      dropShadow: this.bool(item.dropShadow ?? item.drop_shadow),
      shadowColor: this.str(item.shadowColor ?? item.shadow_color),
      shadowBlur: this.num(item.shadowBlur ?? item.shadow_blur),
      shadowOffsetX: this.num(item.shadowOffsetX ?? item.shadow_offset_x),
      shadowOffsetY: this.num(item.shadowOffsetY ?? item.shadow_offset_y),
      x: this.num(item.x),
      y: this.num(item.y),
      width: this.num(item.width),
      height: this.num(item.height),
    };
  }

  private num(v: unknown): number | undefined {
    if (v == null) return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }

  private bool(v: unknown): boolean | undefined {
    if (v == null) return undefined;
    return Boolean(v);
  }

  private str(v: unknown): string | undefined {
    if (v == null) return undefined;
    const s = String(v);
    return s === "" ? undefined : s;
  }

  /**
   * Normalizes destination variants so that textExcludedIds/graphicExcludedIds are always camelCase,
   * accepting either snake_case (text_excluded_ids) or camelCase from the backend.
   */
  private normalizeDestinationVariants(raw: Record<string, unknown> | undefined): Record<string, DestinationVariant> | undefined {
    if (raw == null || typeof raw !== "object") return undefined;
    const result: Record<string, DestinationVariant> = {};
    for (const [destId, variant] of Object.entries(raw)) {
      if (variant == null || typeof variant !== "object") {
        result[destId] = {};
        continue;
      }
      const v = variant as Record<string, unknown>;
      const textIds = v.textExcludedIds ?? v.text_excluded_ids;
      const graphicIds = v.graphicExcludedIds ?? v.graphic_excluded_ids;
      result[destId] = {
        ...v,
        textExcludedIds: Array.isArray(textIds) ? textIds.map(String) : undefined,
        graphicExcludedIds: Array.isArray(graphicIds) ? graphicIds.map(String) : undefined,
      } as DestinationVariant;
    }
    return result;
  }

  private toSaveRequest(
    project: ClippingStudioProject,
    publishPayload?: PublishMetadataSavePayload,
  ): Record<string, unknown> {
    return {
      title: project.title ?? null,
      clip_preview: serializeClipPreviewForProjectSave(project.clipPreview),
      publish_metadata_json:
        publishPayload?.publishMetadataJson ?? "{}",
      publish_metadata_overrides_json:
        publishPayload?.publishMetadataOverridesJson ?? "{}",
      video_segments: JSON.stringify(project.video ?? []),
      markers: JSON.stringify(project.markers ?? []),
      subtitles: JSON.stringify(project.subtitles ?? []),
      // Required by backend model validation; empty edits are represented as an empty object.
      subtitle_edits: JSON.stringify(project.subtitleEdits ?? {}),
      text: JSON.stringify(project.text ?? []),
      graphics: JSON.stringify(project.graphics ?? []),
      audio: JSON.stringify(project.audio ?? []),
      subtitle_style: this.serializeSubtitleStyle(project.subtitleStyle),
      destination_variants:
        project.destinationVariants != null && Object.keys(project.destinationVariants).length > 0
          ? JSON.stringify(project.destinationVariants)
          : "{}",
      appended_mam_segments: JSON.stringify(
        project.appendedMamSegments ?? [],
      ),
      find_clip_recent_prompts: project.findClipRecentPrompts ?? "[]",
    };
  }

  private parseJsonArray<T>(json: string | null | undefined): T[] {
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private parseAudioEvents(
    json: string | null | undefined,
  ): ClippingStudioAudioEvent[] {
    const raw = this.parseJsonArray<Record<string, unknown>>(json);
    return raw
      .map((item) => this.mapOneAudioEvent(item))
      .filter((e): e is ClippingStudioAudioEvent => e != null);
  }

  private mapOneAudioEvent(
    item: Record<string, unknown>,
  ): ClippingStudioAudioEvent | null {
    if (item == null || typeof item !== "object") return null;
    const id = String(item.id ?? "").trim();
    const mediaId = Number(item.mediaId ?? item.media_id);
    const inMs = Number(item.inMs ?? item.in_ms ?? 0);
    const outMs = Number(item.outMs ?? item.out_ms ?? 0);
    if (!id || !Number.isFinite(mediaId) || mediaId < 0) return null;
    const isMainSource =
      item.isMainSource === true || item.is_main_source === true;
    return {
      id,
      mediaId,
      inMs: Number.isFinite(inMs) ? inMs : 0,
      outMs: Number.isFinite(outMs) ? outMs : 0,
      ...(isMainSource ? { isMainSource: true } : {}),
      ...(typeof item.title === "string" ? { title: item.title } : {}),
      ...(typeof item.artist === "string" ? { artist: item.artist } : {}),
      ...(typeof item.durationLabel === "string"
        ? { durationLabel: item.durationLabel }
        : typeof item.duration_label === "string"
          ? { durationLabel: item.duration_label }
          : {}),
      ...(typeof item.artworkUrl === "string"
        ? { artworkUrl: item.artworkUrl }
        : typeof item.artwork_url === "string"
          ? { artworkUrl: item.artwork_url }
          : {}),
      ...(typeof item.artworkColor === "string"
        ? { artworkColor: item.artworkColor }
        : typeof item.artwork_color === "string"
          ? { artworkColor: item.artwork_color }
          : {}),
      ...(item.isBookmarked === true || item.is_bookmarked === true
        ? { isBookmarked: true }
        : {}),
    };
  }

  private parseJsonOrUndefined<T>(
    json: string | null | undefined,
  ): T | undefined {
    if (!json) return undefined;
    try {
      return JSON.parse(json) as T;
    } catch {
      return undefined;
    }
  }
}


